import { useEffect, useState } from "react";
import { X, Target } from "lucide-react";
import { Modal, Field } from "@/components/ui-mm/Modal";
import { useData } from "@/context/DataContext";
import { useConfirm } from "@/context/ConfirmContext";
import { money, today } from "@/lib/money";
import { t } from "@/lib/i18n";
import SavingsGoals from "@/components/mm/SavingsGoals";
import SavingsChart from "@/components/mm/SavingsChart";

export default function SavingsDialog({ open, onClose }) {
  const { db, create, remove, saveSettings, saving } = useData();
  const confirm = useConfirm();
  const prefs = db.settings.prefs;
  const [g, setG] = useState({ goal: "", date: "" });
  const [f, setF] = useState({ amt: "", note: "", date: today() });
  const [account, setAccount] = useState("general");
  const [err, setErr] = useState("");
  useEffect(() => { if (open) setG({ goal: prefs.goal ? String(prefs.goal) : "", date: prefs.goalDate || "" }); }, [open, prefs.goal, prefs.goalDate]);
  const total = db.savings.reduce((t, i) => t + i.amt, 0);

  const move = async (sign) => {
    const a = parseFloat(f.amt);
    if (!(a > 0)) return setErr("Enter an amount first.");
    if (sign < 0 && a > total + 0.001) return setErr("You cannot take out more than you have saved.");
    setErr("");
    try {
      await create("savings", { amt: sign * Math.round(a * 100) / 100, note: f.note.trim(), date: f.date || today(), goal_id: account });
      setF({ amt: "", note: "", date: today() });
    } catch { /* Preserve the pending amount and note after a failed save. */ }
  };
  const setGoal = async () => {
    const v = parseFloat(g.goal);
    if (!(v > 0)) return setErr("Enter the goal amount first.");
    if (!g.date) return setErr("Pick the date you want to reach it by.");
    setErr("");
    await saveSettings({ prefs: { ...prefs, goal: Math.round(v * 100) / 100, goalDate: g.date } }).catch(() => {});
  };
  const clearGoal = async () => { if (await confirm("Remove your savings goal?")) saveSettings({ prefs: { ...prefs, goal: 0, goalDate: "" } }).catch(() => {}); };
  const pct = prefs.goal > 0 ? Math.min(100, (Math.max(0, total) / prefs.goal) * 100) : 0;
  const list = [...db.savings].sort((a, b) => b.date.localeCompare(a.date) || b.created_at - a.created_at).slice(0, 60);

  return (
    <Modal open={open} onClose={onClose} title={t("Savings")} testId="savings-dialog">
      <div className="rounded-[var(--ri)] p-4 text-white" style={{ background: "linear-gradient(135deg,var(--g1),var(--g2))" }}>
        <div className="text-xs uppercase tracking-wider text-white/75">{t("Total saved")}</div>
        <div className="num text-3xl font-bold" data-testid="savings-total">{money(total)}</div>
        {prefs.goal > 0 && <><div className="mt-2 text-sm text-white/85">{pct.toFixed(0)}% of {money(prefs.goal)} by {prefs.goalDate}</div><div className="mt-2 h-2 rounded-full bg-white/25"><i className="block h-full rounded-full bg-white transition-all" style={{ width: `${pct}%` }} /></div></>}
      </div>
      <SavingsChart />
      <SavingsGoals account={account} setAccount={setAccount} />
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Field label={t("Savings goal (optional)")}><input className="mm-input num" type="number" min="0" value={g.goal} onChange={(e) => setG({ ...g, goal: e.target.value })} data-testid="savings-goal-input" /></Field>
        <Field label={t("Target date")}><input className="mm-input" type="date" value={g.date} onChange={(e) => setG({ ...g, date: e.target.value })} data-testid="savings-goal-date-input" /></Field>
      </div>
      <div className="mt-2 flex gap-2">
        <button className="mm-btn sm" onClick={setGoal} data-testid="savings-set-goal-button"><Target className="h-3.5 w-3.5" /> {t("Set goal")}</button>
        {prefs.goal > 0 && <button className="mm-btn ghost sm" onClick={clearGoal} data-testid="savings-clear-goal-button">{t("Clear goal")}</button>}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Field label={t("Amount")}><input className="mm-input num" type="number" step="0.01" min="0" value={f.amt} onChange={(e) => setF({ ...f, amt: e.target.value })} data-testid="savings-amount-input" /></Field>
        <Field label={t("Date")}><input className="mm-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
      </div>
      <input className="mm-input mt-2" placeholder={t("Note (optional)")} maxLength={200} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="savings-note-input" />
      {err && <p className="mt-2 text-sm text-[var(--dg)]" data-testid="savings-error">{err}</p>}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button className="mm-btn justify-center" disabled={saving} onClick={() => move(1)} data-testid="savings-deposit-button">{t("+ Put into savings")}</button>
        <button className="mm-btn ghost justify-center" disabled={saving} onClick={() => move(-1)} data-testid="savings-withdraw-button">{t("− Take out")}</button>
      </div>
      <div className="mt-4 space-y-1">
        {list.map((i) => (
          <div key={i.id} className="flex items-center gap-3 rounded-[var(--ri)] px-2 py-2 hover:bg-[var(--bg)]" data-testid={`savings-item-${i.id}`}>
            <span className="text-xl">{i.transfer_amount ? "↔" : i.amt > 0 ? "🏦" : "↩️"}</span>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{i.amt > 0 ? "Saved" : "Taken out"}</div><div className="truncate text-xs text-[var(--mu)]">{i.date}{i.note ? ` · ${i.note}` : ""}</div></div>
            <b className="num text-sm" style={{ color: i.amt > 0 ? "var(--ok)" : "var(--dg)" }}>{i.amt > 0 ? "+" : ""}{money(i.transfer_amount || i.amt)}</b>
            <button className="grid h-8 w-8 place-items-center rounded-full text-[var(--dg)] hover:bg-[var(--card)]" onClick={async () => { if (await confirm("Delete this savings entry?", { danger: true, yes: "Delete" })) remove("savings", i.id).catch(() => {}); }} aria-label="Delete" data-testid={`savings-delete-${i.id}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
